// src/components/loadletter/RichTextEditor.tsx
//
// Word-style editor for one section of a load letter template (issue #282).
// Admin staff edit visually; the component reads and writes the same stored
// template HTML (with {{field}} placeholders) the raw textareas used to.

import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { TableKit } from '@tiptap/extension-table';
import TextAlign from '@tiptap/extension-text-align';
import {
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Unlink, Table as TableIcon, Undo2, Redo2, Highlighter, Palette, Trash2,
} from 'lucide-react';
import { TemplateFieldNode, StyledBlockNode, PreservedStyles } from './templateEditorExtensions';
import { TemplateField, TEMPLATE_FIELDS, toEditorHtml, fromEditorHtml } from './templateFields';
import './richTextEditor.css';

const TEXT_COLOURS = ['#000000', '#1e3a5f', '#2563eb', '#16a34a', '#b45309', '#dc2626', '#7c3aed', '#6b7280'];
const HIGHLIGHT_COLOURS = ['#FFEB9C', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#e5e7eb'];

// A link may point at a field, e.g. href="{{meaGuidelinesUrl}}" in the default template.
const PLACEHOLDER_URL = /^\{\{[a-zA-Z][a-zA-Z0-9]*\}\}$/;

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  /** Section-only fields (meeting schedule slot, signature image) offered alongside the standard ones. */
  extraFields?: TemplateField[];
  minHeight?: number;
  ariaLabel: string;
}

const ToolbarButton: React.FC<{
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, active, disabled, onClick, children }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={e => e.preventDefault()} // keep the editor's selection
    onClick={onClick}
    className={`h-8 min-w-8 px-1.5 inline-flex items-center justify-center rounded text-sm transition-colors disabled:opacity-40 ${
      active ? 'bg-navy-100 text-navy-800' : 'text-ink-700 hover:bg-ink-100'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-6 bg-ink-200 mx-1" aria-hidden="true" />;

const SwatchMenu: React.FC<{
  label: string;
  icon: React.ReactNode;
  colours: string[];
  onPick: (colour: string) => void;
  onClear: () => void;
}> = ({ label, icon, colours, onPick, onClear }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <ToolbarButton label={label} onClick={() => setOpen(o => !o)}>{icon}</ToolbarButton>
      {open && (
        <div className="absolute z-20 mt-1 p-2 bg-white border border-ink-200 rounded-md shadow-lg flex flex-wrap gap-1 w-40">
          {colours.map(c => (
            <button
              key={c}
              type="button"
              title={c}
              aria-label={`${label} ${c}`}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onPick(c); setOpen(false); }}
              className="h-6 w-6 rounded border border-ink-300"
              style={{ backgroundColor: c }}
            />
          ))}
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => { onClear(); setOpen(false); }}
            className="w-full mt-1 text-xs text-ink-600 hover:text-ink-900 text-left"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

const Toolbar: React.FC<{ editor: Editor; fields: TemplateField[] }> = ({ editor, fields }) => {
  const chain = () => editor.chain().focus();
  const inTable = editor.isActive('table');

  const blockType = editor.isActive('heading', { level: 1 }) ? 'h1'
    : editor.isActive('heading', { level: 2 }) ? 'h2'
    : editor.isActive('heading', { level: 3 }) ? 'h3'
    : 'p';

  const setBlockType = (value: string) => {
    if (value === 'p') chain().setParagraph().run();
    else chain().setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 }).run();
  };

  const setLink = () => {
    const current = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link address (web address, or a field such as {{meaGuidelinesUrl}})', current ?? 'https://');
    if (url === null) return;
    if (!url.trim()) chain().extendMarkRange('link').unsetLink().run();
    else chain().extendMarkRange('link').setLink({ href: url.trim() }).run();
  };

  const groups = Array.from(new Set(fields.map(f => f.group)));

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-ink-200 bg-ink-50 rounded-t-md" role="toolbar">
      <select
        aria-label="Text style"
        className="h-8 text-sm border-ink-300 rounded py-0 pl-2 pr-7"
        value={blockType}
        onChange={e => setBlockType(e.target.value)}
      >
        <option value="p">Normal text</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>
      <Divider />
      <ToolbarButton label="Bold" active={editor.isActive('bold')} onClick={() => chain().toggleBold().run()}><Bold className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Italic" active={editor.isActive('italic')} onClick={() => chain().toggleItalic().run()}><Italic className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Underline" active={editor.isActive('underline')} onClick={() => chain().toggleUnderline().run()}><Underline className="h-4 w-4" /></ToolbarButton>
      <SwatchMenu
        label="Text colour"
        icon={<Palette className="h-4 w-4" />}
        colours={TEXT_COLOURS}
        onPick={c => chain().setColor(c).run()}
        onClear={() => chain().unsetColor().run()}
      />
      <SwatchMenu
        label="Highlight"
        icon={<Highlighter className="h-4 w-4" />}
        colours={HIGHLIGHT_COLOURS}
        onPick={c => chain().setHighlight({ color: c }).run()}
        onClear={() => chain().unsetHighlight().run()}
      />
      <Divider />
      <ToolbarButton label="Bullet list" active={editor.isActive('bulletList')} onClick={() => chain().toggleBulletList().run()}><List className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Numbered list" active={editor.isActive('orderedList')} onClick={() => chain().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></ToolbarButton>
      <Divider />
      <ToolbarButton label="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => chain().setTextAlign('left').run()}><AlignLeft className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Align centre" active={editor.isActive({ textAlign: 'center' })} onClick={() => chain().setTextAlign('center').run()}><AlignCenter className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => chain().setTextAlign('right').run()}><AlignRight className="h-4 w-4" /></ToolbarButton>
      <Divider />
      <ToolbarButton label="Add or edit link" active={editor.isActive('link')} onClick={setLink}><LinkIcon className="h-4 w-4" /></ToolbarButton>
      {editor.isActive('link') && (
        <ToolbarButton label="Remove link" onClick={() => chain().extendMarkRange('link').unsetLink().run()}><Unlink className="h-4 w-4" /></ToolbarButton>
      )}
      <ToolbarButton label="Insert table" onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon className="h-4 w-4" /></ToolbarButton>
      {inTable && (
        <>
          <ToolbarButton label="Add row below" onClick={() => chain().addRowAfter().run()}><span className="text-xs">+ Row</span></ToolbarButton>
          <ToolbarButton label="Add column right" onClick={() => chain().addColumnAfter().run()}><span className="text-xs">+ Col</span></ToolbarButton>
          <ToolbarButton label="Delete row" onClick={() => chain().deleteRow().run()}><span className="text-xs">− Row</span></ToolbarButton>
          <ToolbarButton label="Delete column" onClick={() => chain().deleteColumn().run()}><span className="text-xs">− Col</span></ToolbarButton>
          <ToolbarButton label="Delete table" onClick={() => chain().deleteTable().run()}><Trash2 className="h-4 w-4" /></ToolbarButton>
        </>
      )}
      <Divider />
      <ToolbarButton label="Undo" disabled={!editor.can().undo()} onClick={() => chain().undo().run()}><Undo2 className="h-4 w-4" /></ToolbarButton>
      <ToolbarButton label="Redo" disabled={!editor.can().redo()} onClick={() => chain().redo().run()}><Redo2 className="h-4 w-4" /></ToolbarButton>
      <div className="ml-auto">
        <select
          aria-label="Insert field"
          className="h-8 text-sm rounded py-0 pl-2 pr-7 bg-navy-600 text-white border-navy-600"
          value=""
          onChange={e => {
            const field = e.target.value;
            if (field) chain().insertContent({ type: 'templateField', attrs: { field } }).insertContent(' ').run();
          }}
        >
          <option value="">+ Insert field</option>
          {groups.map(g => (
            <optgroup key={g} label={g}>
              {fields.filter(f => f.group === g).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, extraFields = [], minHeight = 120, ariaLabel }) => {
  // The HTML this editor last emitted. When `value` changes to anything else
  // (Reset, or the template reloading after Save) the content is replaced.
  const lastEmitted = useRef<string>(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
          isAllowedUri: (url, ctx) => PLACEHOLDER_URL.test(url) || ctx.defaultValidate(url),
        },
      }),
      TextStyleKit,
      Highlight.configure({ multicolor: true }),
      TableKit.configure({ table: { resizable: false } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TemplateFieldNode,
      StyledBlockNode,
      PreservedStyles,
    ],
    content: toEditorHtml(value),
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: {
        class: 'rte-content focus:outline-none px-4 py-3',
        style: `min-height: ${minHeight}px`,
        'aria-label': ariaLabel,
        'aria-multiline': 'true',
      },
    },
    // Only real edits emit, so an untouched section is saved exactly as it was
    // stored rather than in the editor's normalised form.
    onUpdate: ({ editor: ed }) => {
      const html = fromEditorHtml(ed.getHTML());
      lastEmitted.current = html;
      onChangeRef.current(html);
    },
  });

  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(toEditorHtml(value), { emitUpdate: false });
  }, [editor, value]);

  // A section stored as plain text (no tags) prints as one run-on block, so hand
  // the converted HTML straight back: the next Save fixes it without an edit.
  useEffect(() => {
    if (!editor || !value.trim() || /<[a-z][\s\S]*>/i.test(value)) return;
    const html = fromEditorHtml(editor.getHTML());
    lastEmitted.current = html;
    onChangeRef.current(html);
    // Only on mount / when a new plain-text value arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  return (
    <div className="border border-ink-300 rounded-md bg-white focus-within:ring-2 focus-within:ring-navy-500 focus-within:border-navy-500">
      {editor && <Toolbar editor={editor} fields={[...TEMPLATE_FIELDS, ...extraFields]} />}
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
